/**
 * Transform a single workspace to a FlowNode
 * The workspace becomes the root node with workspace-specific metadata
 */
export function transformWorkspaceToNode(workspace) {
    const metadata = {
        id: workspace.id,
        name: workspace.name,
        role: workspace.role,
        isOwner: workspace.isOwner,
        memberCount: undefined, // Can be populated if member data is available
    };
    return {
        id: `workspace-${workspace.id}`,
        type: "workspace",
        position: { x: 0, y: 0 }, // Will be calculated by layout engine
        data: {
            type: "workspace",
            label: workspace.name,
            metadata,
        },
    };
}
/**
 * Transform project data to FlowNode array
 * Each project becomes a node linked to its parent workspace
 */
export function transformProjectsToNodes(projects) {
    return projects.map((project) => {
        const metadata = {
            id: project.id,
            name: project.name,
            status: project.status || "to_do", // Default to "to_do" if status is null
            workspaceId: project.workspaceId,
            taskCount: project.taskCount, // Use the taskCount from the Project type
        };
        return {
            id: `project-${project.id}`,
            type: "project",
            position: { x: 0, y: 0 }, // Will be calculated by layout engine
            data: {
                type: "project",
                label: project.name,
                metadata,
            },
        };
    });
}
/**
 * Transform task data to FlowNode array
 * Each task becomes a node linked to its parent project
 */
export function transformTasksToNodes(tasks) {
    return tasks.map((task) => {
        const metadata = {
            id: task.id,
            title: task.title,
            status: task.status,
            priority: task.priority,
            projectId: task.projectId,
        };
        return {
            id: `task-${task.id}`,
            type: "task",
            position: { x: 0, y: 0 }, // Will be calculated by layout engine
            data: {
                type: "task",
                label: task.title,
                metadata,
            },
        };
    });
}
/**
 * Transform notification data to FlowNode array
 * Each notification becomes a node that can be linked to related entities
 */
export function transformNotificationsToNodes(notifications) {
    return notifications.map((notification) => {
        // Determine related entity type based on notification type
        let relatedEntityType;
        if (notification.type === "task_assignment" ||
            notification.type === "task_comment" ||
            notification.type === "due_date_reminder" ||
            notification.type === "overdue_task") {
            relatedEntityType = "task";
        }
        else if (notification.type === "project_update") {
            relatedEntityType = "project";
        }
        else if (notification.type === "workspace_invite") {
            relatedEntityType = "workspace";
        }
        const metadata = {
            id: notification.id,
            message: notification.message || notification.title,
            isRead: !!notification.readAt,
            relatedEntityId: notification.relatedId,
            relatedEntityType,
        };
        return {
            id: `notification-${notification.id}`,
            type: "notification",
            position: { x: 0, y: 0 }, // Will be calculated by layout engine
            data: {
                type: "notification",
                label: notification.title,
                metadata,
            },
        };
    });
}
/**
 * Create edges between nodes based on their relationships
 * - Workspace contains Projects
 * - Project contains Tasks
 * - Notification notifies related entities
 */
export function createEdges(nodes) {
    const edges = [];
    // Create a map for quick node lookup
    const nodeMap = new Map();
    nodes.forEach((node) => {
        nodeMap.set(node.id, node);
    });
    nodes.forEach((node) => {
        // Create edges from projects to their parent workspaces
        if (node.data.type === "project") {
            const projectData = node.data.metadata;
            const workspaceNodeId = `workspace-${projectData.workspaceId}`;
            if (nodeMap.has(workspaceNodeId)) {
                edges.push({
                    id: `edge-${workspaceNodeId}-${node.id}`,
                    source: workspaceNodeId,
                    target: node.id,
                    type: "default",
                    data: {
                        relationship: "contains",
                    },
                });
            }
        }
        // Create edges from tasks to their parent projects
        if (node.data.type === "task") {
            const taskData = node.data.metadata;
            const projectNodeId = `project-${taskData.projectId}`;
            if (nodeMap.has(projectNodeId)) {
                edges.push({
                    id: `edge-${projectNodeId}-${node.id}`,
                    source: projectNodeId,
                    target: node.id,
                    type: "default",
                    data: {
                        relationship: "contains",
                    },
                });
            }
        }
        // Create edges from notifications to their related entities
        if (node.data.type === "notification") {
            const notificationData = node.data.metadata;
            if (notificationData.relatedEntityId && notificationData.relatedEntityType) {
                const relatedNodeId = `${notificationData.relatedEntityType}-${notificationData.relatedEntityId}`;
                if (nodeMap.has(relatedNodeId)) {
                    edges.push({
                        id: `edge-${node.id}-${relatedNodeId}`,
                        source: node.id,
                        target: relatedNodeId,
                        type: "default",
                        data: {
                            relationship: "notifies",
                        },
                    });
                }
            }
        }
    });
    return edges;
}
/**
 * Aggregate all data transformations into a single flow data structure
 * Combines a single workspace with its projects, tasks, and notifications into nodes and edges
 */
export function aggregateFlowData(workspace, projects, tasks, notifications) {
    // Transform all entities to nodes
    const workspaceNode = transformWorkspaceToNode(workspace);
    const projectNodes = transformProjectsToNodes(projects);
    const taskNodes = transformTasksToNodes(tasks);
    const notificationNodes = transformNotificationsToNodes(notifications);
    // Combine all nodes
    const nodes = [
        workspaceNode,
        ...projectNodes,
        ...taskNodes,
        ...notificationNodes,
    ];
    // Create edges based on relationships
    const edges = createEdges(nodes);
    return { nodes, edges };
}
